import { MdClose } from "react-icons/md";
import styles from "./upgrade-agency-modal.module.css";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useServerAction } from "zsa-react";
import { upgradeToAgencyAction } from "../../actions";
import { toast } from "react-toastify";
import { useTranslations } from "next-intl";
import { ApiError } from "@/types/types";
import { useUserDataContext } from "../../user-data-context";
import { useDialogRouting } from "@/hooks/use-dialog-routing";

type FormData = {
  fullName: string;
  role: string;
  agencyName: string;

  foundedYear: string;
  agentsCount: string;
  website: string;
  address: string;
  avgListings: string;
  locations: string;

  specializations: Record<string, boolean>;
  acquisition: Record<string, boolean>;

  priceRange: string;
  motivation: string;
};

export default function UpgradeAgencyModal({
  isOpen,
  dialogRef,
  closeModal,
}: {
  isOpen: boolean;
  dialogRef: RefObject<HTMLDivElement | null>;
  closeModal: () => void;
}) {
  const { execute: upgradeToAgencyExecute } = useServerAction(
    upgradeToAgencyAction,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const t = useTranslations("MyAccount.UpgradeModal");
  const { state, updateField } = useUserDataContext();
  const [formData, setFormData] = useState<FormData>({
    // Identity
    fullName: "",
    role: "",
    agencyName: "",

    // Agency facts
    foundedYear: "",
    agentsCount: "",
    website: "",
    address: "",
    avgListings: "",
    locations: "",

    // Specializations (boolean map)
    specializations: {
      residential: false,
      luxury: false,
      offPlan: false,
      commercial: false,
      rentals: false,
      land: false,
      investment: false,
    },

    // Acquisition methods (boolean map)
    acquisition: {
      directOwners: false,
      exclusiveContracts: false,
      developerPartnerships: false,
      mls: false,
      referrals: false,
    },

    // Pricing & intent
    priceRange: "",
    motivation: "",
  });

  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      closeBtnRef.current?.focus();
    }
  }, [isOpen]);

  useDialogRouting("UpgradeAgencyModal", isOpen, closeModal);

  const toggleCheckbox = (
    group: "specializations" | "acquisition",
    key: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        [key]: !prev[group][key],
      },
    }));
  };

  const updateFormField = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        e.stopPropagation();
        closeModal();
      }}
    >
      <div
        ref={dialogRef}
        className={`${styles.modal} ${isOpen ? styles.show : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-agency-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <h2 id="upgrade-agency-title" className={styles.title}>
            {t("title")}
          </h2>
          <p className={styles.subtitle}>{t("subTitle")}</p>
          <button
            ref={closeBtnRef}
            className={styles.closeBtn}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              closeModal();
            }}
            aria-label={t("closeModal")}
          >
            <MdClose />
          </button>
        </header>

        <main className={styles.body}>
          {/* === Identity === */}
          <label htmlFor="full-name" className={styles.label}>
            {t("fullNameLabel")}
          </label>
          <input
            id="full-name"
            type="text"
            maxLength={70}
            placeholder={t("fullNamePlaceholder")}
            className={styles.input}
            value={formData.fullName}
            onChange={(e) => updateFormField("fullName", e.target.value)}
          />

          <label htmlFor="role" className={styles.label}>
            {t("roleLabel")}
          </label>
          <select
            id="role"
            className={styles.input}
            value={formData.role}
            onChange={(e) => updateFormField("role", e.target.value)}
          >
            <option value="">{t("selectRole")}</option>
            <option value="founder">{t("roles.founder")}</option>
            <option value="managing_director">
              {t("roles.managing_director")}
            </option>
            <option value="operations_manager">
              {t("roles.operations_manager")}
            </option>
            <option value="team_lead">{t("roles.team_lead")}</option>
            <option value="agent">{t("roles.agent")}</option>
          </select>

          <label htmlFor="agency-name" className={styles.label}>
            {t("agencyName")}
          </label>
          <input
            id="agency-name"
            type="text"
            maxLength={70}
            placeholder={t("agencyNamePlaceholder")}
            className={styles.input}
            value={formData.agencyName}
            onChange={(e) => updateFormField("agencyName", e.target.value)}
          />

          {/* === Two-column block === */}
          <div className={styles.formGrid}>
            <div>
              <label htmlFor="founded-year" className={styles.label}>
                {t("foundedYearLabel")}
              </label>
              <input
                id="founded-year"
                type="text"
                inputMode="numeric"
                placeholder={t("foundedYearPlaceholder")}
                className={styles.input}
                value={formData.foundedYear}
                onChange={(e) => updateFormField("foundedYear", e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="agents-count" className={styles.label}>
                {t("agentsCountLabel")}
              </label>
              <input
                id="agents-count"
                type="text"
                inputMode="numeric"
                placeholder={t("agentsCountPlaceholder")}
                className={styles.input}
                value={formData.agentsCount}
                onChange={(e) => updateFormField("agentsCount", e.target.value)}
              />
            </div>
          </div>

          {/* === Web & location === */}
          <label htmlFor="website" className={styles.label}>
            {t("websiteLabel")}
          </label>
          <input
            id="website"
            type="url"
            placeholder={t("websitePlaceholder")}
            className={styles.input}
            value={formData.website}
            onChange={(e) => updateFormField("website", e.target.value)}
          />

          <label htmlFor="address" className={styles.label}>
            {t("addressLabel")}
          </label>
          <input
            id="address"
            type="text"
            placeholder={t("addressPlaceholder")}
            className={styles.input}
            value={formData.address}
            onChange={(e) => updateFormField("address", e.target.value)}
          />

          {/* === Operations === */}
          <label htmlFor="avg-listings" className={styles.label}>
            {t("avgListingsLabel")}
          </label>
          <input
            id="avg-listings"
            type="text"
            inputMode="numeric"
            placeholder={t("avgListingsPlaceholder")}
            className={styles.input}
            value={formData.avgListings}
            onChange={(e) => updateFormField("avgListings", e.target.value)}
          />

          <label htmlFor="locations" className={styles.label}>
            {t("locationsLabel")}
          </label>
          <input
            id="locations"
            type="text"
            placeholder={t("locationsPlaceholder")}
            className={styles.input}
            value={formData.locations}
            onChange={(e) => updateFormField("locations", e.target.value)}
          />

          {/* === CHECKBOX GROUP: Specializations === */}
          <fieldset className={styles.checkboxGroup}>
            <legend className={styles.label}>
              {t("specializationsLabel")}
            </legend>

            {[
              "residential",
              "luxury",
              "offPlan",
              "commercial",
              "rentals",
              "land",
              "investment",
            ].map((key) => {
              const id = `specialization-${key}`;

              return (
                <label key={key} htmlFor={id} className={styles.checkboxItem}>
                  <input
                    id={id}
                    type="checkbox"
                    className={styles.checkBox}
                    checked={
                      formData.specializations[
                        key as keyof typeof formData.specializations
                      ]
                    }
                    onChange={() => toggleCheckbox("specializations", key)}
                  />
                  {t(`specializations.${key}`)}
                </label>
              );
            })}
          </fieldset>

          {/* === CHECKBOX GROUP: Listing sourcing === */}
          <fieldset className={styles.checkboxGroup}>
            <legend className={styles.label}>{t("acquisitionLabel")}</legend>

            {[
              "directOwners",
              "exclusiveContracts",
              "developerPartnerships",
              "mls",
              "referrals",
            ].map((key) => {
              const id = `acquisition-${key}`;

              return (
                <label key={key} htmlFor={id} className={styles.checkboxItem}>
                  <input
                    id={id}
                    type="checkbox"
                    className={styles.checkBox}
                    checked={
                      formData.acquisition[
                        key as keyof typeof formData.acquisition
                      ]
                    }
                    onChange={() => toggleCheckbox("acquisition", key)}
                  />
                  {t(`acquisition.${key}`)}
                </label>
              );
            })}
          </fieldset>

          {/* === Market positioning === */}
          <label htmlFor="price-range" className={styles.label}>
            {t("priceRangeLabel")}
          </label>
          <select
            id="price-range"
            className={styles.input}
            value={formData.priceRange}
            onChange={(e) => updateFormField("priceRange", e.target.value)}
          >
            <option value="">{t("selectPriceRange")}</option>
            <option value="under_100k">{t("priceRanges.under_100k")}</option>
            <option value="100k_300k">{t("priceRanges.100k_300k")}</option>
            <option value="300k_750k">{t("priceRanges.300k_750k")}</option>
            <option value="750k_2m">{t("priceRanges.750k_2m")}</option>
            <option value="2m_plus">{t("priceRanges.2m_plus")}</option>
          </select>

          {/* === Intent === */}
          <label htmlFor="motivation" className={styles.label}>
            {t("motivationLabel")}
          </label>
          <textarea
            id="motivation"
            maxLength={500}
            rows={4}
            placeholder={t("motivationPlaceholder")}
            className={styles.input}
            style={{ resize: "vertical" }}
            value={formData.motivation}
            onChange={(e) => updateFormField("motivation", e.target.value)}
          />
        </main>

        <footer className={styles.footer}>
          <button
            className={styles.cancelBtn}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              closeModal();
            }}
          >
            {t("cancel")}
          </button>
          <button
            className={styles.createBtn}
            disabled={isSubmitting}
            aria-disabled={isSubmitting}
            onClick={async (e) => {
              e.preventDefault();
              setIsSubmitting(true);
              const [data, err] = await upgradeToAgencyExecute({
                full_name: formData.fullName,
                role: formData.role,
                agency_name: formData.agencyName,
                founded_year: formData.foundedYear,
                agents_count: formData.agentsCount,
                website: formData.website || null,
                address: formData.address,
                avg_listings: formData.avgListings,
                locations: formData.locations,
                price_range: formData.priceRange,
                motivation: formData.motivation,
                specializations: {
                  residential: formData.specializations.residential,
                  luxury: formData.specializations.luxury,
                  off_plan: formData.specializations.offPlan,
                  commercial: formData.specializations.commercial,
                  rentals: formData.specializations.rentals,
                  land: formData.specializations.land,
                  investment: formData.specializations.investment,
                },
                acquisition: {
                  direct_owners: formData.acquisition.directOwners,
                  exclusive_contracts: formData.acquisition.exclusiveContracts,
                  developer_partnerships:
                    formData.acquisition.developerPartnerships,
                  mls: formData.acquisition.mls,
                  referrals: formData.acquisition.referrals,
                },
              });

              if (err?.name === "ZodError") {
                const errors = Object.values(err.fieldErrors ?? {}).flat();

                toast(
                  errors.length
                    ? t(`errors.${errors[0]}`)
                    : t("errors.invalidFormData"),
                  {
                    type: "error",
                  },
                );

                setIsSubmitting(false);
                return;
              }
              if (err || isApiError(data)) {
                toast(
                  data && isApiError(data)
                    ? data.message
                    : t("errors.submitError"),
                  { type: "error" },
                );
                setIsSubmitting(false);
                return;
              }
              updateField("applications", [data, ...state.applications]);
              setIsSubmitting(false);
              closeModal();
            }}
          >
            {t("submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}

function isApiError(value: any): value is ApiError {
  return (
    value &&
    typeof value === "object" &&
    typeof value.message === "string" &&
    typeof value.error_code === "string" &&
    "http_code" in value
  );
}
